import React, { useState, useRef, useEffect, useCallback } from 'react';
import './DetailsSection.css';

const PREDEFINED_KEYS = [
  'gender',
  'maiden_name',
  'middle_name',
  'nationality'
];

const VALUE_SUGGESTIONS = {
  gender: ['Male', 'Female', 'Other']
};

// Helper to convert details object to fields array
function detailsToFields(details, nextIdRef) {
  const entries = Object.entries(details || {});
  return entries.map(([key, value]) => ({
    id: nextIdRef.current++,
    key,
    value
  }));
}

function DetailsSection({ details, onChange }) {
  const nextIdRef = useRef(0);
  const [fields, setFields] = useState(() => detailsToFields(details, nextIdRef));
  const isInternalChange = useRef(false);

  // Sync fields when details prop changes from parent (e.g., data loaded)
  // But skip if we triggered the change ourselves
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }

    // Only sync if the incoming details are meaningfully different
    const currentDetails = {};
    fields.forEach(({ key, value }) => {
      if (key) currentDetails[key] = value;
    });

    const incomingKeys = Object.keys(details || {}).sort().join(',');
    const currentKeys = Object.keys(currentDetails).sort().join(',');
    const incomingValues = Object.values(details || {}).sort().join(',');
    const currentValues = Object.values(currentDetails).sort().join(',');

    if (incomingKeys !== currentKeys || incomingValues !== currentValues) {
      setFields(detailsToFields(details, nextIdRef));
    }
  }, [details]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update fields and notify parent
  const updateFields = useCallback((newFields) => {
    isInternalChange.current = true;
    setFields(newFields);

    const newDetails = {};
    newFields.forEach(({ key, value }) => {
      if (key) {
        newDetails[key] = value;
      }
    });
    onChange(newDetails);
  }, [onChange]);

  const handleAddField = () => {
    const usedKeys = fields.map(f => f.key);
    const availableKey = PREDEFINED_KEYS.find(k => !usedKeys.includes(k)) || '';

    updateFields([...fields, { id: nextIdRef.current++, key: availableKey, value: '' }]);
  };

  const handleKeyChange = (id, newKey) => {
    // Check for duplicate keys (but allow empty keys during typing)
    if (newKey && fields.some(f => f.id !== id && f.key === newKey)) {
      alert('This key already exists');
      return;
    }

    updateFields(fields.map(f => f.id === id ? { ...f, key: newKey } : f));
  };

  const handleValueChange = (id, value) => {
    updateFields(fields.map(f => f.id === id ? { ...f, value } : f));
  };

  const handleDeleteField = (id) => {
    updateFields(fields.filter(f => f.id !== id));
  };

  return (
    <div className="details-section">
      {fields.length > 0 && (
        <div className="details-grid">
          {fields.map(({ id, key, value }) => (
            <div key={id} className="detail-row">
              <div className="detail-key">
                <input
                  type="text"
                  list={`key-suggestions-${id}`}
                  value={key}
                  onChange={(e) => handleKeyChange(id, e.target.value)}
                  placeholder="Key"
                  className="detail-input"
                />
                <datalist id={`key-suggestions-${id}`}>
                  {PREDEFINED_KEYS.map(predefinedKey => (
                    <option key={predefinedKey} value={predefinedKey} />
                  ))}
                </datalist>
              </div>

              <div className="detail-value">
                <input
                  type="text"
                  list={`value-suggestions-${id}`}
                  value={value}
                  onChange={(e) => handleValueChange(id, e.target.value)}
                  placeholder="Value"
                  className="detail-input"
                />
                {VALUE_SUGGESTIONS[key] && (
                  <datalist id={`value-suggestions-${id}`}>
                    {VALUE_SUGGESTIONS[key].map(suggestion => (
                      <option key={suggestion} value={suggestion} />
                    ))}
                  </datalist>
                )}
              </div>

              <button
                type="button"
                className="detail-delete-btn"
                onClick={() => handleDeleteField(id)}
                aria-label="Delete field"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        type="button"
        className="add-field-btn"
        onClick={handleAddField}
      >
        + Add field
      </button>
    </div>
  );
}

export default DetailsSection;
